/**
 * <mellow-slider>: keeps the filled track (--_percent), the value output and the optional
 * number field in sync with its range input – also when Livewire or other scripts set
 * .value programmatically. Typing in the number field updates the range and fires its
 * input/change events, so wire:model and listeners on the range keep working.
 */
export class MellowSlider extends HTMLElement {
    connectedCallback() {
        this.input = this.querySelector('input[type="range"]');
        if (!this.input || this.input._mellowSlider) return;
        this.input._mellowSlider = true;

        const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
        const update = () => this.update();

        Object.defineProperty(this.input, 'value', {
            configurable: true,
            get() {
                return descriptor.get.call(this);
            },
            set(value) {
                descriptor.set.call(this, value);
                update();
            },
        });

        this.input.addEventListener('input', update);

        const number = this.number;
        if (number) {
            number.addEventListener('input', (event) => {
                event.stopPropagation();
                this.fromNumber('input');
            });
            number.addEventListener('change', (event) => {
                event.stopPropagation();
                this.fromNumber('change');
                // Show the value the range settled on (clamped, snapped to step).
                number.value = this.input.value;
            });
        }

        this.update();
    }

    get number() {
        return this.querySelector('[data-mellow-slider-number]');
    }

    /** Apply a typed value to the range – ignoring incomplete input like "-" or "". */
    fromNumber(type) {
        const raw = this.number.value;
        if (raw === '' || Number.isNaN(Number(raw))) return;

        const previous = this.input.value;
        this.input.value = raw; // the browser clamps to min/max and snaps to step
        if (type === 'change' || this.input.value !== previous) {
            this.input.dispatchEvent(new Event(type, { bubbles: true }));
        }
    }

    update() {
        const { min, max, value } = this.input;
        const lower = Number(min || 0);
        const upper = Number(max || 100);
        const percent = upper > lower ? ((Number(value) - lower) / (upper - lower)) * 100 : 0;

        this.style.setProperty('--_percent', `${percent}%`);

        const output = this.querySelector('.mellow-slider-value');
        if (output) output.textContent = `${value}${this.dataset.suffix ?? ''}`;

        // Don't overwrite what the user is typing.
        const number = this.number;
        if (number && document.activeElement !== number && number.value !== value) number.value = value;
    }
}
